import React, { useState } from 'react';
import { useTranslations } from '../context/TranslationContext';
import Header from '../sections/Header'; // Import the Header component

interface TermsPageProps {
  companyName?: string;
  companyAddress?: string;
  contactEmail?: string;
  minimumAge?: number;
}

const TermsPage: React.FC<TermsPageProps> = ({
  companyName = "LegisRatio",
  companyAddress = "Piazzale Lavater, 3, Milano, 20133",
  contactEmail = "info@legisratio.it",
  minimumAge = 18,
}) => {
  const { t } = useTranslations();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-void-blue text-white overflow-y-auto">
      {/* Header */}
      <Header isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} closeMenu={closeMenu} hideNavLinks={true} />


      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 mt-16"> {/* Added mt-16 to push content down from fixed header */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="font-display text-4xl font-bold text-white mb-4">
              {t('terms_header_title')}
            </h1>
          </div>

          <div className="space-y-6 text-gray-300">
            <div className="mb-6">
              <p className="mb-4">{t('terms_intro_governs')}</p>
              <ul className="list-disc pl-6 mb-4">
                <li>{t('terms_intro_website_use')}</li>
                <li>{t('terms_intro_related_agreements')}</li>
              </ul>
              <p className="mb-4">{t('terms_intro_legally_binding')}</p>
              <p className="mb-4 font-semibold">{t('terms_intro_read_carefully')}</p>
              <p className="mb-4">
                {t('terms_intro_generated_with')}{' '}
                <a
                  href="https://www.iubenda.com/en/terms-and-conditions-generator"
                  className="text-mystic-violet hover:text-cosmic-teal underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('terms_intro_generator_link')}
                </a>.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="font-display text-3xl font-bold text-white mb-4">{t('terms_glance_title')}</h2>
              <p className="mb-4">{t('terms_glance_description')}</p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-3xl font-bold text-white mb-4">{t('terms_usage_title')}</h2>
              <p className="mb-4">{t('terms_usage_general_apply')}</p>
              <p className="mb-4">{t('terms_usage_specific_conditions')}</p>
              <p className="mb-4">{t('terms_usage_by_using')}</p>
              <ul className="list-disc pl-6 mb-6">
                <li>{t('terms_usage_age_requirement', { minimumAge })}</li>
                <li>{t('terms_usage_embargo_country')}</li>
                <li>{t('terms_usage_prohibited_list')}</li>
              </ul>

              <h3 className="font-display text-2xl font-semibold text-white mb-3">{t('terms_account_registration_title')}</h3>
              <p className="mb-4">{t('terms_account_registration_description')}</p>
              <p className="mb-4">{t('terms_account_registration_responsibility')}</p>
              <p className="mb-4">{t('terms_account_registration_full_responsibility')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_account_conditions_title')}</h4>
              <p className="mb-4">{t('terms_account_conditions_description')}</p>
              <ul className="list-disc pl-6 mb-6">
                <li>{t('terms_account_conditions_no_bots')}</li>
                <li>{t('terms_account_conditions_one_account')}</li>
                <li>{t('terms_account_conditions_no_sharing')}</li>
              </ul>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_account_termination_title')}</h4>
              <p className="mb-4">{t('terms_account_termination_description')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_account_suspension_title')}</h4>
              <p className="mb-4">{t('terms_account_suspension_right_to_suspend')}</p>
              <p className="mb-4">{t('terms_account_suspension_no_compensation')}</p>
              <p className="mb-4">{t('terms_account_suspension_no_exemption')}</p>
            </section>

            <section className="mb-8">
              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_content_title')}</h3>
              <p className="mb-4">{t('terms_content_ownership')}</p>
              <p className="mb-4">{t('terms_content_compliance')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_content_rights_title')}</h4>
              <p className="mb-4">{t('terms_content_rights_all_rights')}</p>
              <p className="mb-4">{t('terms_content_rights_proper_use')}</p>
              <p className="mb-4">{t('terms_content_rights_prohibited')}</p>
              <p className="mb-4">{t('terms_content_rights_personal_use')}</p>
              <p className="mb-4">{t('terms_content_rights_statutory')}</p>
            </section>

            <section className="mb-8">
              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_external_title')}</h3>
              <p className="mb-4">{t('terms_external_no_control')}</p>
              <p className="mb-4">{t('terms_external_third_party_terms')}</p>
            </section>

            <section className="mb-8">
              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_acceptable_use_title')}</h3>
              <p className="mb-4">{t('terms_acceptable_use_scope')}</p>
              <p className="mb-4">{t('terms_acceptable_use_responsibility')}</p>
              <p className="mb-4">{t('terms_acceptable_use_right_to_protect')}</p>
              <ul className="list-disc pl-6 mb-6">
                <li>{t('terms_acceptable_use_violating_laws')}</li>
                <li>{t('terms_acceptable_use_infringing')}</li>
                <li>{t('terms_acceptable_use_impairing')}</li>
                <li>{t('terms_acceptable_use_offending')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">{t('terms_liability_title')}</h2>
              <p className="mb-4">{t('terms_liability_limitation')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_liability_indemnification_title')}</h4>
              <p className="mb-4">{t('terms_liability_indemnification_description')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_liability_limitation_title')}</h4>
              <p className="mb-4">{t('terms_liability_limitation_description')}</p>
              <p className="mb-4">{t('terms_liability_exceptions')}</p>
              <p className="mb-4">{t('terms_liability_typical_damages')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">{t('terms_common_title')}</h2>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_no_waiver_title')}</h3>
              <p className="mb-4">{t('terms_common_no_waiver_description')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_service_interruption_title')}</h3>
              <p className="mb-4">{t('terms_common_service_interruption_maintenance')}</p>
              <p className="mb-4">{t('terms_common_service_interruption_discontinuation')}</p>
              <p className="mb-4">{t('terms_common_service_interruption_unavailable')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_reselling_title')}</h3>
              <p className="mb-4">{t('terms_common_reselling_description')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_privacy_title')}</h3>
              <p className="mb-4">{t('terms_common_privacy_description')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_intellectual_property_title')}</h3>
              <p className="mb-4">{t('terms_common_intellectual_property_rights')}</p>
              <p className="mb-4">{t('terms_common_intellectual_property_trademarks')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_changes_title')}</h3>
              <p className="mb-4">{t('terms_common_changes_right_to_modify')}</p>
              <p className="mb-4">{t('terms_common_changes_affect_relationship')}</p>
              <p className="mb-4">{t('terms_common_changes_continued_use')}</p>
              <p className="mb-4">{t('terms_common_changes_previous_version')}</p>
              <p className="mb-4">{t('terms_common_changes_advance_notice')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_assignment_title')}</h3>
              <p className="mb-4">{t('terms_common_assignment_our_right')}</p>
              <p className="mb-4">{t('terms_common_assignment_your_restriction')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_contact_title')}</h3>
              <p className="mb-4">{t('terms_common_contact_description')}</p>
              <div className="bg-deep-space p-4 rounded-lg mb-4">
                <p className="text-white font-medium">{t('terms_common_contact_info')}</p>
                <p>{companyAddress}</p>
                <p>
                  {t('terms_common_contact_email')} <a href={`mailto:${contactEmail}`} className="text-mystic-violet hover:text-cosmic-teal underline">
                    {contactEmail}
                  </a>
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_severability_title')}</h3>
              <p className="mb-4">{t('terms_common_severability_description')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_governing_law_title')}</h3>
              <p className="mb-4">{t('terms_common_governing_law_description')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_common_governing_law_prevalence_title')}</h4>
              <p className="mb-4">{t('terms_common_governing_law_prevalence_description')}</p>

              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_common_jurisdiction_title')}</h3>
              <p className="mb-4">{t('terms_common_jurisdiction_description')}</p>

              <h4 className="text-xl font-medium text-white mb-3">{t('terms_common_jurisdiction_exception_title')}</h4>
              <p className="mb-4">{t('terms_common_jurisdiction_exception_description')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">{t('terms_dispute_title')}</h2>
              <h3 className="text-2xl font-semibold text-white mb-3">{t('terms_dispute_online_title')}</h3>
              <p className="mb-4">{t('terms_dispute_online_commission')}</p>
              <p className="mb-4">
                {t('terms_dispute_online_platform')}{' '}
                <a
                  href="https://ec.europa.eu/consumers/odr/main/index.cfm?event=main.home.chooseLanguage"
                  className="text-mystic-violet hover:text-cosmic-teal underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('terms_dispute_online_link')}
                </a>.
              </p>
            </section>

            <footer className="mt-12 pt-8 border-t border-white/10">
              <p className="text-sm text-gray-400">
                {t('terms_footer_last_updated')}: {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-400 mt-2">
                {t('terms_footer_generated')}{' '}
                <a
                  href="https://www.iubenda.com/en/terms-and-conditions-generator"
                  className="text-mystic-violet hover:text-cosmic-teal underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('terms_footer_generator_link')}
                </a>.
              </p>
            </footer>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
